/**
 * Versión que SIRVE este dominio, leída de `/version.json`.
 *
 * ─── Por qué del propio dominio y no del backend ni del bucket ─────────────
 *
 * El fichero se genera en el build (plugin `rois-version-json` de
 * `vite.config.js`) y Vercel lo publica en el MISMO despliegue atómico que los
 * JS: lo que dice es, por construcción, lo que se está sirviendo. Antes la web
 * preguntaba al backend, cuya versión la escribe un cron que elige uno de los
 * tres despliegues del tag (Vercel prod, respaldo, Android): anunciaba
 * versiones que aún no se servían y el aviso volvía en bucle (spec 2026-09-30
 * § 2.1). El `latest.json` del bucket es de la APK y lo publica otro workflow.
 */

export interface WebVersionInfo {
  version: string;
  /** Release forzada (`npm run release:*:mandatory`): aviso sin X. */
  mandatory: boolean;
}

export const WEB_VERSION_URL = '/version.json';

/**
 * Pide al hook (`useAppUpdate`) una comprobación inmediata, p. ej. cuando el
 * Service Worker detecta una versión nueva. Un evento y no una llamada
 * directa: `main.tsx` no tiene acceso al hook, y así el aviso se abre desde un
 * solo sitio.
 */
export const CHECK_UPDATE_EVENT = 'rois:check-update';

/** Interruptor SOLO de pruebas: activa las comprobaciones fuera de producción. */
export const WEB_CHECKS_OVERRIDE_KEY = 'rois.update.webChecks';

export async function fetchWebVersion(): Promise<WebVersionInfo | null> {
  try {
    const res = await fetch(`${WEB_VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    // 🔴 Si el fichero no existe, Vercel responde `index.html` con 200: el
    // `json()` lanza y se trata igual que sin red.
    const data: unknown = await res.json();
    if (!data || typeof data !== 'object') return null;
    const { version, mandatory } = data as Record<string, unknown>;
    if (typeof version !== 'string' || version.length === 0) return null;
    return { version, mandatory: mandatory === true };
  } catch {
    return null;
  }
}

/**
 * El aviso web solo existe en producción (beta: decisión D6 pendiente). El
 * interruptor de `localStorage` lo enciende en las E2E, que se compilan en modo
 * desarrollo, sin afectar al resto de tests.
 */
export function isWebUpdateEnabled(): boolean {
  if (import.meta.env.MODE === 'production') return true;
  try {
    return localStorage.getItem(WEB_CHECKS_OVERRIDE_KEY) === '1';
  } catch {
    return false;
  }
}
